import { Portal } from '@ark-ui/react/portal'
import { Building2, CalendarDays, CreditCard, LayoutDashboard, LogOut, Menu, Mic2, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { signOut } from '../../lib/auth.client'
import { Box, Flex, HStack, VStack, styled } from '../../styled-system/jsx'
import { Button, Dialog } from '../ui'

interface SelfServiceShellProps {
    readonly children: ReactNode
    readonly currentDate: string
    readonly user: {
        readonly name: string
        readonly email: string
    }
}

const ShellLink = styled(Link)

const navigation = [
    { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, enabled: true },
    { label: 'Venues', href: '/venues', icon: Building2, enabled: true },
    { label: 'Billing', href: '/billing', icon: CreditCard, enabled: true },
    { label: 'Artists', href: '/artists', icon: Mic2, enabled: false },
    { label: 'Events', href: '/events', icon: CalendarDays, enabled: false },
] as const

export function SelfServiceShell({ children, currentDate, user }: SelfServiceShellProps) {
    const location = useLocation()
    const [mobileOpen, setMobileOpen] = useState(false)
    const [sidebarOpen, setSidebarOpen] = useState(true)

    const handleSignOut = () => {
        void signOut().then(() => {
            window.location.href = '/login'
        })
    }

    const renderNavigation = () => (
        <VStack alignItems="stretch" gap="1">
            {navigation.map((item) => {
                const active = item.href === '/dashboard'
                    ? location.pathname === item.href
                    : location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)

                if (!item.enabled) {
                    return (
                        <HStack
                            key={item.label}
                            gap="3"
                            px="3"
                            py="2.5"
                            color="white.a5"
                            borderRadius="md"
                            cursor="not-allowed"
                            aria-disabled="true"
                        >
                            <item.icon size={18} aria-hidden="true" />
                            <styled.span flex="1" fontSize="sm" fontWeight="medium">{item.label}</styled.span>
                        </HStack>
                    )
                }

                return (
                    <ShellLink
                        key={item.label}
                        to={item.href}
                        onClick={() => setMobileOpen(false)}
                        aria-current={active ? 'page' : undefined}
                        display="flex"
                        alignItems="center"
                        gap="3"
                        px="3"
                        py="2.5"
                        borderRadius="md"
                        bg={active ? 'brand.lemonLime' : 'transparent'}
                        color={active ? 'brand.raisinBlack' : 'white.a7'}
                        textDecoration="none"
                        fontSize="sm"
                        fontWeight="semibold"
                        _hover={{ bg: active ? 'brand.lemonLime' : 'white.a2', color: active ? 'brand.raisinBlack' : 'white' }}
                    >
                        <item.icon size={18} aria-hidden="true" />
                        {item.label}
                    </ShellLink>
                )
            })}
        </VStack>
    )

    const sidebarContent = (
        <Flex h="full" flexDirection="column">
            <Box px="5" py="5" borderBottomWidth="1px" borderColor="white.a2">
                <ShellLink to="/dashboard" display="inline-flex" alignItems="center" gap="3" textDecoration="none">
                    <styled.img
                        src="/FINAL_entx_black.png"
                        alt="ENTX"
                        h="9"
                        w="auto"
                        className="brand-logo-on-dark"
                    />
                </ShellLink>
                <styled.p mt="3" color="white.a6" fontSize="xs" textTransform="uppercase" fontWeight="semibold">
                    Self-Service Portal
                </styled.p>
            </Box>

            <styled.nav flex="1" px="4" py="5" aria-label="Self-Service">
                {renderNavigation()}
            </styled.nav>

            <Box px="4" py="4" borderTopWidth="1px" borderColor="white.a2">
                <HStack gap="3" px="2" mb="3" minW="0">
                    <VStack alignItems="start" gap="0" minW="0" flex="1">
                        <styled.span color="white" fontSize="sm" fontWeight="semibold" truncate>{user.name}</styled.span>
                        <styled.span color="white.a5" fontSize="xs" truncate>{user.email}</styled.span>
                    </VStack>
                </HStack>
                <Button
                    variant="plain"
                    w="full"
                    justifyContent="flex-start"
                    gap="3"
                    color="white.a7"
                    _hover={{ color: 'white', bg: 'white.a2' }}
                    onClick={handleSignOut}
                >
                    <LogOut size={18} aria-hidden="true" />
                    Sign out
                </Button>
            </Box>
        </Flex>
    )

    return (
        <Flex minH="dvh" bg="brand.jet" color="fg.default">
            <styled.a
                href="#self-service-content"
                position="fixed"
                top="2"
                left="2"
                zIndex="modal"
                px="3"
                py="2"
                bg="brand.lemonLime"
                color="brand.raisinBlack"
                borderRadius="md"
                transform="translateY(-150%)"
                _focus={{ transform: 'translateY(0)' }}
            >
                Skip to content
            </styled.a>

            <styled.aside
                display={{ base: 'none', lg: 'block' }}
                position="sticky"
                top="0"
                h="dvh"
                w={sidebarOpen ? '64' : '0'}
                flexShrink="0"
                bg="bg.header"
                color="white"
                borderRightWidth="1px"
                borderColor="white.a2"
                overflow="hidden"
                transition="size"
            >
                <Box w="64" h="full">{sidebarContent}</Box>
            </styled.aside>

            <Dialog.Root open={mobileOpen} onOpenChange={({ open }) => setMobileOpen(open)}>
                <Portal>
                    <Dialog.Backdrop display={{ base: 'block', lg: 'none' }} />
                    <Dialog.Positioner
                        display={{ base: 'flex', lg: 'none' }}
                        justifyContent="flex-start"
                        alignItems="stretch"
                        p="0"
                    >
                        <Dialog.Content
                            h="dvh"
                            w="80"
                            maxW="11/12"
                            m="0"
                            borderRadius="xs"
                            bg="bg.header"
                            color="white"
                            boxShadow="xl"
                            position="relative"
                        >
                            <Dialog.Title srOnly>Self-Service navigation</Dialog.Title>
                            <Dialog.CloseTrigger asChild>
                                <Button
                                    position="absolute"
                                    top="4"
                                    right="3"
                                    zIndex="docked"
                                    variant="plain"
                                    color="white.a7"
                                    aria-label="Close navigation"
                                >
                                    <X size={21} aria-hidden="true" />
                                </Button>
                            </Dialog.CloseTrigger>
                            {sidebarContent}
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            <Flex flex="1" minW="0" flexDirection="column">
                <HStack
                    position="sticky"
                    top="0"
                    zIndex="docked"
                    h="16"
                    px={{ base: '4', md: '6' }}
                    justify="space-between"
                    bg="bg.header"
                    color="white"
                    borderBottomWidth="1px"
                    borderColor="white.a2"
                >
                    <HStack gap="3">
                        <Button
                            display={{ base: 'inline-flex', lg: 'none' }}
                            variant="plain"
                            color="white"
                            aria-label="Open navigation"
                            onClick={() => setMobileOpen(true)}
                        >
                            <Menu size={22} aria-hidden="true" />
                        </Button>
                        <Button
                            display={{ base: 'none', lg: 'inline-flex' }}
                            variant="plain"
                            color="white.a7"
                            aria-label={sidebarOpen ? 'Collapse navigation' : 'Expand navigation'}
                            title={sidebarOpen ? 'Collapse navigation' : 'Expand navigation'}
                            onClick={() => setSidebarOpen((open) => !open)}
                        >
                            {sidebarOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
                        </Button>
                        <VStack display={{ base: 'flex', lg: 'none' }} alignItems="start" gap="0">
                            <styled.span fontSize="sm" fontWeight="semibold">Self-Service Portal</styled.span>
                            <styled.span fontSize="xs" color="white.a5">ENTX</styled.span>
                        </VStack>
                    </HStack>
                    <styled.time
                        display={{ base: 'none', sm: 'block' }}
                        minW={{ sm: '64' }}
                        textAlign="right"
                        color="white.a7"
                        fontSize="sm"
                    >
                        {currentDate}
                    </styled.time>
                </HStack>

                <styled.main id="self-service-content" flex="1" minW="0">
                    {children}
                </styled.main>
            </Flex>
        </Flex>
    )
}
